import { useEffect } from 'react';

const BASE = 'https://marsresume.com';

/**
 * The homepage-only JSON-LD block (WebPage + FAQPage) ships statically in
 * index.html, so in a SPA it would otherwise follow the user onto every
 * route — putting the homepage URL and the product FAQ on pages they do
 * not describe. We detach it whenever a route supplies its own schema and
 * re-attach it on cleanup.
 */
let detachedHomeGraph = null;

const setHomeGraphEnabled = (enabled) => {
  if (typeof document === 'undefined') return;

  if (enabled) {
    if (detachedHomeGraph && !detachedHomeGraph.isConnected) {
      document.head.appendChild(detachedHomeGraph);
    }
    return;
  }

  const el = document.querySelector('script[data-seo="home"]');
  if (el) {
    detachedHomeGraph = el;
    el.remove();
  }
};

export const usePageSEO = ({
  title, description, canonical, keywords, robots,
  ogDescription, ogImage, ogImageAlt,
  twitterDescription, twitterImage, twitterImageAlt,
  jsonLd,
  noindex = false,
}) => {
  // Serialized here so the effect keys off content rather than object
  // identity — callers can build the graph inline without thrashing.
  // Escaping "<" keeps a stray "</script>" in post content from breaking
  // out of the tag; the sequence stays valid JSON either way.
  const nodes = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
  const jsonLdText = nodes.length
    ? JSON.stringify(nodes.length === 1 ? nodes[0] : nodes).replace(/</g, '\\u003c')
    : '';

  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el && value) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', keywords);
    setMeta('meta[name="robots"]', 'content',
      robots || (noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')
    );

    // Open Graph
    const url = canonical ? `${BASE}${canonical}` : BASE;
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', ogDescription || description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[property="og:image:alt"]', 'content', ogImageAlt);

    // Twitter
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', twitterDescription || description);
    setMeta('meta[name="twitter:image"]', 'content', twitterImage || ogImage);
    setMeta('meta[name="twitter:image:alt"]', 'content', twitterImageAlt || ogImageAlt);

    // Google / itemprop
    setMeta('meta[itemprop="name"]', 'content', title);
    setMeta('meta[itemprop="description"]', 'content', description);
    setMeta('meta[itemprop="image"]', 'content', ogImage);

    // Canonical link
    let canonEl = document.querySelector('link[rel="canonical"]');
    if (!canonEl) {
      canonEl = document.createElement('link');
      canonEl.rel = 'canonical';
      document.head.appendChild(canonEl);
    }
    canonEl.href = url;

    // Structured data (JSON-LD)
    const clearPageGraph = () =>
      document.querySelectorAll('script[data-seo="page"]').forEach(el => el.remove());

    clearPageGraph();

    if (jsonLdText) {
      setHomeGraphEnabled(false);
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'page');
      script.textContent = jsonLdText;
      document.head.appendChild(script);
    } else {
      setHomeGraphEnabled(true);
    }

    return () => {
      clearPageGraph();
      setHomeGraphEnabled(true);
    };
  }, [title, description, canonical, keywords, robots, ogDescription, ogImage, ogImageAlt, twitterDescription, twitterImage, twitterImageAlt, jsonLdText, noindex]);
};
